import { Element } from '../Element';

/**
 * TextElement class representing a text element in the timeline
 */
export class TextElement extends Element {
  // Core text properties
  text: string = '';
  font_family: string = 'Aileron';
  font_weight: number | string = 400;
  font_style: string = 'normal';
  font_size: number | string | null = null;
  font_size_minimum: string = '1 vmin';
  font_size_maximum: string = '100 vmin';
  // Note: fill_color, x_alignment, y_alignment are now inherited from Element base class

  // Text alignment and spacing
  letter_spacing: string = '0%';
  line_height: string = '115%';

  // Text behavior
  text_wrap: boolean = true;
  text_clip: boolean = false;
  text_transform: string = 'none'; // none, capitalize, uppercase, lowercase

  // Background properties
  background_color: string | null = null;
  background_x_padding: string = '25%';
  background_y_padding: string = '25%';
  background_border_radius: string = '0%';
  background_align_threshold: string = '20%';

  // Transcript properties
  transcript_source: string | null = null;
  transcript_effect: string = 'color'; // color, karaoke, highlight, fade, bounce, slide, enlarge
  transcript_split: string = 'word'; // none, word, line
  transcript_placement: string = 'static'; // static, animate, align
  transcript_maximum_length: number | null = null;
  transcript_color: string = '#e74c3c';

  constructor(props: Partial<TextElement> = {}) {
    super({ ...props, type: 'text' });

    // Core text properties
    this.text = props.text ?? '';
    this.font_family =
      props.font_family ?? (props as any).fontFamily ?? 'Aileron';
    this.font_weight = props.font_weight ?? 400;
    this.font_style = props.font_style ?? 'normal';
    this.font_size = props.font_size ?? (props as any).fontSize ?? null;
    this.font_size_minimum = props.font_size_minimum ?? '1 vmin';
    this.font_size_maximum = props.font_size_maximum ?? '100 vmin';

    this._height = props.height ?? null; // TextElement default
    this._width = props.width ?? null; // TextElement default

    // Set TextElement-specific defaults for inherited properties
    // Override Element defaults with TextElement-specific values
    super.update({
      fill_color: props.fill_color ?? (props as any).color ?? '#000000',
      x_alignment: props.x_alignment ?? '0%',
      y_alignment: props.y_alignment ?? '0%',
    });

    // Text alignment and spacing
    this.letter_spacing = props.letter_spacing ?? '0%';
    this.line_height = props.line_height ?? '115%';

    // Text behavior
    this.text_wrap = props.text_wrap ?? true;
    this.text_clip = props.text_clip ?? false;
    this.text_transform = props.text_transform ?? 'none';

    // Background properties
    this.background_color = props.background_color ?? null;
    this.background_x_padding = props.background_x_padding ?? '25%';
    this.background_y_padding = props.background_y_padding ?? '25%';
    this.background_border_radius = props.background_border_radius ?? '0%';
    this.background_align_threshold = props.background_align_threshold ?? '20%';

    // Transcript properties
    this.transcript_source = props.transcript_source ?? null;
    this.transcript_effect = props.transcript_effect ?? 'color';
    this.transcript_split = props.transcript_split ?? 'word';
    this.transcript_placement = props.transcript_placement ?? 'static';
    this.transcript_maximum_length = props.transcript_maximum_length ?? null;
    this.transcript_color = props.transcript_color ?? '#e74c3c';
  }

  /**
   * Captures the mutable state of the text element.
   * @returns A record of all mutable properties.
   */
  override getState(): Record<string, any> {
    const state = {
      ...super.getState(),
      // Core text properties
      text: this.text,
      font_family: this.font_family,
      font_weight: this.font_weight,
      font_style: this.font_style,
      font_size: this.font_size,
      font_size_minimum: this.font_size_minimum,
      font_size_maximum: this.font_size_maximum,
      // Note: fill_color, x_alignment, y_alignment are now included in super.getState()
      // Text alignment and spacing
      letter_spacing: this.letter_spacing,
      line_height: this.line_height,
      // Text behavior
      text_wrap: this.text_wrap,
      text_clip: this.text_clip,
      text_transform: this.text_transform,
      // Background properties
      background_color: this.background_color,
      background_x_padding: this.background_x_padding,
      background_y_padding: this.background_y_padding,
      background_border_radius: this.background_border_radius,
      background_align_threshold: this.background_align_threshold,
      // Transcript properties
      transcript_source: this.transcript_source,
      transcript_effect: this.transcript_effect,
      transcript_split: this.transcript_split,
      transcript_placement: this.transcript_placement,
      transcript_maximum_length: this.transcript_maximum_length,
      transcript_color: this.transcript_color,
    };

    return state;
  }

  /**
   * Restores the text element's mutable state from a state record.
   * @param state The state to restore.
   */
  override restoreState(state: Record<string, any>): void {
    super.restoreState(state);

    // Core text properties
    if (state.text !== undefined) this.text = state.text;
    if (state.font_family !== undefined) this.font_family = state.font_family;
    if (state.font_weight !== undefined) this.font_weight = state.font_weight;
    if (state.font_style !== undefined) this.font_style = state.font_style;
    if (state.font_size !== undefined) this.font_size = state.font_size;
    if (state.font_size_minimum !== undefined)
      this.font_size_minimum = state.font_size_minimum;
    if (state.font_size_maximum !== undefined)
      this.font_size_maximum = state.font_size_maximum;
    // Note: fill_color, x_alignment, y_alignment are now handled by Element.restoreState()

    // Text alignment and spacing
    if (state.letter_spacing !== undefined)
      this.letter_spacing = state.letter_spacing;
    if (state.line_height !== undefined) this.line_height = state.line_height;

    // Text behavior
    if (state.text_wrap !== undefined) this.text_wrap = state.text_wrap;
    if (state.text_clip !== undefined) this.text_clip = state.text_clip;
    if (state.text_transform !== undefined)
      this.text_transform = state.text_transform;

    // Background properties
    if (state.background_color !== undefined)
      this.background_color = state.background_color;
    if (state.background_x_padding !== undefined)
      this.background_x_padding = state.background_x_padding;
    if (state.background_y_padding !== undefined)
      this.background_y_padding = state.background_y_padding;
    if (state.background_border_radius !== undefined)
      this.background_border_radius = state.background_border_radius;
    if (state.background_align_threshold !== undefined)
      this.background_align_threshold = state.background_align_threshold;

    // Transcript properties
    if (state.transcript_source !== undefined)
      this.transcript_source = state.transcript_source;
    if (state.transcript_effect !== undefined)
      this.transcript_effect = state.transcript_effect;
    if (state.transcript_split !== undefined)
      this.transcript_split = state.transcript_split;
    if (state.transcript_placement !== undefined)
      this.transcript_placement = state.transcript_placement;
    if (state.transcript_maximum_length !== undefined)
      this.transcript_maximum_length = state.transcript_maximum_length;
    if (state.transcript_color !== undefined)
      this.transcript_color = state.transcript_color;
  }

  /**
   * Updates the text element's properties
   * @param props Properties to update
   */
  override update(props: Partial<TextElement>): void {
    super.update(props);

    // Core text properties
    if (props.text !== undefined) this.text = props.text;
    if (props.font_family !== undefined) this.font_family = props.font_family;
    if (props.font_weight !== undefined) this.font_weight = props.font_weight;
    if (props.font_style !== undefined) this.font_style = props.font_style;
    if (props.font_size !== undefined) this.font_size = props.font_size;
    if (props.font_size_minimum !== undefined)
      this.font_size_minimum = props.font_size_minimum;
    if (props.font_size_maximum !== undefined)
      this.font_size_maximum = props.font_size_maximum;
    // Note: fill_color, x_alignment, y_alignment are now handled by Element.update()

    // Text alignment and spacing
    if (props.letter_spacing !== undefined)
      this.letter_spacing = props.letter_spacing;
    if (props.line_height !== undefined) this.line_height = props.line_height;

    // Text behavior
    if (props.text_wrap !== undefined) this.text_wrap = props.text_wrap;
    if (props.text_clip !== undefined) this.text_clip = props.text_clip;
    if (props.text_transform !== undefined)
      this.text_transform = props.text_transform;

    // Background properties
    if (props.background_color !== undefined)
      this.background_color = props.background_color;
    if (props.background_x_padding !== undefined)
      this.background_x_padding = props.background_x_padding;
    if (props.background_y_padding !== undefined)
      this.background_y_padding = props.background_y_padding;
    if (props.background_border_radius !== undefined)
      this.background_border_radius = props.background_border_radius;
    if (props.background_align_threshold !== undefined)
      this.background_align_threshold = props.background_align_threshold;

    // Transcript properties
    if (props.transcript_source !== undefined)
      this.transcript_source = props.transcript_source;
    if (props.transcript_effect !== undefined)
      this.transcript_effect = props.transcript_effect;
    if (props.transcript_split !== undefined)
      this.transcript_split = props.transcript_split;
    if (props.transcript_placement !== undefined)
      this.transcript_placement = props.transcript_placement;
    if (props.transcript_maximum_length !== undefined)
      this.transcript_maximum_length = props.transcript_maximum_length;
    if (props.transcript_color !== undefined)
      this.transcript_color = props.transcript_color;
  }

  protected override getAdditionalProperties(): Record<string, any> {
    const properties: Record<string, any> = {
      // Core text properties
      text: this.text,
      font_family: this.font_family,
      font_weight: this.font_weight,
      font_style: this.font_style,
      font_size: this.font_size,
      font_size_minimum: this.font_size_minimum,
      font_size_maximum: this.font_size_maximum,
      // Note: fill_color, x_alignment, y_alignment have different default values than in Element.toJSON()
      x_alignment: this.x_alignment,
      y_alignment: this.y_alignment,
      fill_color: this.fill_color,
      // Text alignment and spacing
      letter_spacing: this.letter_spacing,
      line_height: this.line_height,
      // Text behavior
      text_wrap: this.text_wrap,
      text_clip: this.text_clip,
      text_transform: this.text_transform,
      // Background properties
      background_color: this.background_color,
      background_x_padding: this.background_x_padding,
      background_y_padding: this.background_y_padding,
      background_border_radius: this.background_border_radius,
      background_align_threshold: this.background_align_threshold,
      // Transcript properties
      transcript_source: this.transcript_source,
      transcript_effect: this.transcript_effect,
      transcript_split: this.transcript_split,
      transcript_placement: this.transcript_placement,
      transcript_maximum_length: this.transcript_maximum_length,
      transcript_color: this.transcript_color,
    };

    return properties;
  }

  /**
   * Creates a deep clone of this text element
   * Override the base Element.clone() to ensure we create a TextElement, not a generic Element
   */
  override clone(): TextElement {
    const el = new TextElement({
      ...this.getState(),
    });

    return el;
  }

  /**
   * Creates a TextElement from JSON data
   */
  static fromJSON(json: Record<string, any>): TextElement {
    // Parse time-based properties that Element would usually parse
    const parsedTime = Element.parseTimeProperty(
      json.time ?? json.start ?? json.globalTime ?? 0,
    );
    const parsedDuration = Element.parseTimeProperty(json.duration);

    // Ensure parsedTime is a number, default to 0 if null
    const timeValue = parsedTime !== null ? Number(parsedTime) : 0;

    return new TextElement({
      // Base element properties
      id: json.id,
      track: json.track ?? null,
      time: timeValue,
      duration: parsedDuration,
      type: 'text', // Force text type
      dynamic: json.dynamic ?? false,
      locked: json.locked ?? false,
      visible: json.visible ?? true,
      x: json.x ?? '50%',
      y: json.y ?? '50%',
      width: json.width ?? null, // TextElement default
      height: json.height ?? null, // TextElement default

      // Transform properties (now handled by Element)
      aspect_ratio: json.aspect_ratio ?? null,
      x_padding: json.x_padding ?? '0 vw',
      y_padding: json.y_padding ?? '0 vh',
      z_index: json.z_index ?? null,
      x_anchor: json.x_anchor ?? '50%',
      y_anchor: json.y_anchor ?? '50%',
      x_scale: json.x_scale ?? '100%',
      y_scale: json.y_scale ?? '100%',
      x_skew: json.x_skew ?? '0',
      y_skew: json.y_skew ?? '0',
      x_rotation: json.x_rotation ?? '0',
      y_rotation: json.y_rotation ?? '0',
      z_rotation: json.z_rotation ?? '0',
      perspective: json.perspective ?? null,
      backface_visible: json.backface_visible ?? true,

      // Animation support (now handled by Element)
      animations: Array.isArray(json.animations) ? json.animations : [],

      // Core text properties
      text: json.text ?? '',
      font_family: json.font_family ?? json.fontFamily ?? 'Aileron',
      font_weight: json.font_weight ?? 400,
      font_style: json.font_style ?? 'normal',
      font_size: json.font_size ?? json.fontSize ?? null,
      font_size_minimum: json.font_size_minimum ?? '1 vmin',
      font_size_maximum: json.font_size_maximum ?? '100 vmin',
      // Note: fill_color, x_alignment, y_alignment will be set by Element constructor
      // Override with TextElement-specific defaults
      fill_color: json.fill_color ?? json.color ?? '#000000',
      x_alignment: json.x_alignment ?? '0%',
      y_alignment: json.y_alignment ?? '0%',

      // Text alignment and spacing
      letter_spacing: json.letter_spacing ?? '0%',
      line_height: json.line_height ?? '115%',

      // Text behavior
      text_wrap: json.text_wrap ?? true,
      text_clip: json.text_clip ?? false,
      text_transform: json.text_transform ?? 'none',

      // Background properties
      background_color: json.background_color ?? null,
      background_x_padding: json.background_x_padding ?? '25%',
      background_y_padding: json.background_y_padding ?? '25%',
      background_border_radius: json.background_border_radius ?? '0%',
      background_align_threshold: json.background_align_threshold ?? '20%',

      // Transcript properties
      transcript_source: json.transcript_source ?? null,
      transcript_effect: json.transcript_effect ?? 'color',
      transcript_split: json.transcript_split ?? 'word',
      transcript_placement: json.transcript_placement ?? 'static',
      transcript_maximum_length: json.transcript_maximum_length ?? null,
      transcript_color: json.transcript_color ?? '#e74c3c',
    });
  }
}
